import { z } from 'zod';
import { LanguageLevel } from '../domain/common';

/**
 * Common validation patterns and utilities
 * Reusable across all domain and API schemas
 */

/**
 * Common string validation patterns
 */
export const ValidationPatterns = {
  /** Email validation */
  email: z
    .email({ message: 'Invalid email format' })
    .max(254, 'Email too long'),

  /** MongoDB ObjectId validation */
  objectId: z.string().regex(/^[0-9a-fA-F]{24}$/, 'Invalid ObjectId format'),

  flexibleObjectId: z
    .union([
      z.string().regex(/^[0-9a-fA-F]{24}$/, 'Invalid ObjectId format'), // String ObjectId
      z
        .any()
        .refine(
          val =>
            val &&
            typeof val === 'object' &&
            val.toString &&
            typeof val.toString === 'function',
          'Must be a valid ObjectId or string'
        ),
    ])
    .transform(val => {
      if (typeof val === 'string') return val;
      if (val && typeof val === 'object' && val.toString) {
        return val.toString();
      }
      return String(val);
    }),

  /** UUID validation */
  uuid: z.uuid({ message: 'Invalid UUID format' }),

  /** Phone number in E.164 format */
  phoneE164: z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, 'Invalid E.164 phone number format'),

  /** Timezone validation (IANA timezone names) */
  timezone: z
    .string()
    .regex(/^[A-Za-z_]+\/[A-Za-z_]+$/, 'Invalid timezone format'),

  /** Language level validation */
  languageLevel: z.nativeEnum(LanguageLevel),

  /** Hour validation (0-23) */
  hour: z
    .number()
    .int()
    .min(0, 'Hour must be 0-23')
    .max(23, 'Hour must be 0-23'),

  /** Words per day validation (1-50) */
  wordsPerDay: z
    .number()
    .int()
    .min(1, 'Words per day must be at least 1')
    .max(50, 'Words per day cannot exceed 50'),

  /** URL validation */
  url: z.url('Invalid URL format'),

  flexibleDate: z
    .union([z.date(), z.iso.datetime()])
    .transform(val => (val instanceof Date ? val.toISOString() : val)),

  /** Non-empty string with length constraints */
  nonEmptyString: (
    minLength: number = 1,
    maxLength: number = 255,
    fieldName: string = 'Field'
  ) =>
    z
      .string()
      .min(minLength, `${fieldName} must be at least ${minLength} characters`)
      .max(maxLength, `${fieldName} cannot exceed ${maxLength} characters`),

  /** JWT token validation */
  jwtToken: z
    .string()
    .min(1, 'Token is required')
    .regex(
      /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/,
      'Invalid JWT format'
    ),

  /** Google OAuth token validation */
  googleOAuthToken: z
    .string()
    .min(1, 'Google OAuth token is required')
    .max(2048, 'Token is too long'),
};

/**
 * Common schema utilities
 */
export const SchemaUtils = {
  /** Create a paginated response schema */
  createPaginatedResponse: <T extends z.ZodType>(itemSchema: T) =>
    z.object({
      items: z.array(itemSchema),
      total: z.number().int().min(0),
      limit: z.number().int().min(1),
      offset: z.number().int().min(0),
      hasMore: z.boolean(),
    }),
};

/**
 * Standard timestamp schemas
 */
export const TimestampSchemas = {
  /** ISO datetime string */
  datetime: z.iso.datetime('Invalid datetime format'),

  /** Created/updated timestamps */
  createdAt: z.iso.datetime().describe('Creation timestamp'),
  updatedAt: z.iso.datetime().describe('Last update timestamp'),

  /** Full timestamp object for entities */
  timestamps: z.object({
    createdAt: z.iso.datetime().describe('Creation timestamp'),
    updatedAt: z.iso.datetime().describe('Last update timestamp'),
  }),
};

/**
 * Common field schemas
 */
export const CommonFields = {
  /** Standard ID field */
  id: ValidationPatterns.nonEmptyString(1, 50, 'ID').describe(
    'Unique identifier'
  ),

  /** MongoDB ObjectId field */
  mongoId: ValidationPatterns.objectId.describe('MongoDB ObjectId'),

  /** User display name */
  displayName: ValidationPatterns.nonEmptyString(1, 255, 'Name').describe(
    'Display name'
  ),

  /** Email field */
  email: ValidationPatterns.email.describe('Email address'),

  /** Profile picture URL */
  profilePicture: ValidationPatterns.url
    .optional()
    .describe('Profile picture URL'),

  /** Optional profile picture with empty string default */
  profilePictureWithDefault: z
    .string()
    .default('')
    .describe('Profile picture URL'),
};

/**
 * Unified API response types
 */
export type ApiResponse<T = unknown> = {
  success: boolean;
  data: T | null;
  message: string;
  error?: {
    message: string;
    code: string;
    details?: unknown;
  };
  meta?: {
    timestamp: string;
    requestId?: string;
  };
};

export type ApiSuccessResponse<T = unknown> = {
  success: true;
  data: T;
  message: string;
  meta?: {
    timestamp: string;
    requestId?: string;
  };
};

export type ApiErrorResponse = {
  success: false;
  data: null;
  message: string;
  error: {
    message: string;
    code: string;
    details?: unknown;
  };
  meta?: {
    timestamp: string;
    requestId?: string;
  };
};

export const Unlimited = {
  Number: -1,
  Constant: 'unlimited',
};
